/**
 * UTM + signup-source capture (added 2026-05-01).
 *
 * The 4/30 audit found that 0 of 13 BragBox signups had a recorded source. Every
 * sale we get for the next 30 days would be untraceable without this. The
 * standing rule is in `_shared/utm-capture-rule.md` — every link Peace ships
 * (warm-graph text, Pinterest pin, Meta ad, email CTA, Stripe/Payhip checkout)
 * carries `utm_source`, `utm_medium`, `utm_campaign`. This module reads them
 * off the URL on first landing and persists them across the session so the
 * signup payload can attach them.
 *
 * Stored under one localStorage key (a JSON blob) so it travels with the user
 * even if they wander between landing pages before signing up. The capture is
 * "first touch wins" — once we've captured a non-default source we don't
 * overwrite it on subsequent navigations within the same session.
 */

const STORAGE_KEY = 'bragbox_utm_v1';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function safeStorageGet() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.captured_at && Date.now() - parsed.captured_at > TTL_MS) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function safeStorageSet(payload) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    // Quota / disabled storage. Fail silently.
  }
}

/**
 * Read UTM params off the current URL. Returns an object with whatever was
 * present (or null fields if absent).
 */
export function readUtmFromUrl() {
  if (typeof window === 'undefined') return {};
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      // signup_source is an explicit override the warm-graph links can carry
      // (e.g. ?signup_source=warm) — usually equals utm_source for paid channels.
      signup_source: params.get('signup_source') || params.get('utm_source'),
    };
  } catch (e) {
    return {};
  }
}

/**
 * First-touch capture. Run on every page load. If localStorage already has a
 * payload from a real (non-direct) source, don't overwrite. If the URL carries
 * UTMs, capture them. Otherwise no-op.
 */
export function captureUtmsFromLanding() {
  if (typeof window === 'undefined') return;

  const urlUtms = readUtmFromUrl();
  const hasUrlSource = !!(urlUtms.utm_source || urlUtms.signup_source);

  const stored = safeStorageGet();
  const storedHasSource = !!(stored && (stored.utm_source || stored.signup_source) && stored.signup_source !== 'direct');

  // If we already have a real source captured, leave it alone (first-touch wins).
  if (storedHasSource && !hasUrlSource) return;

  // If neither URL nor storage has a source, capture a "direct" baseline so
  // `signup_source` is never null on the eventual signup payload.
  if (!hasUrlSource && !stored) {
    safeStorageSet({
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      signup_source: 'direct',
      referrer: document.referrer || null,
      landing_page: window.location.pathname || '/',
      captured_at: Date.now(),
    });
    return;
  }

  // URL carries UTMs: capture them. This is the path warm-graph/Pinterest/Meta
  // links will trigger.
  if (hasUrlSource) {
    safeStorageSet({
      utm_source: urlUtms.utm_source || null,
      utm_medium: urlUtms.utm_medium || null,
      utm_campaign: urlUtms.utm_campaign || null,
      signup_source: urlUtms.signup_source || urlUtms.utm_source || null,
      referrer: document.referrer || null,
      landing_page: window.location.pathname || '/',
      captured_at: Date.now(),
    });
  }
}

/**
 * Read whatever's been captured. Returns an object suitable for spreading into
 * the signup request body. All fields can be null.
 */
export function getCapturedUtms() {
  const stored = safeStorageGet();
  if (!stored) {
    return {
      signup_source: 'direct',
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      referrer: typeof document !== 'undefined' ? (document.referrer || null) : null,
      landing_page: typeof window !== 'undefined' ? (window.location.pathname || '/') : null,
    };
  }
  return {
    signup_source: stored.signup_source || 'direct',
    utm_source: stored.utm_source || null,
    utm_medium: stored.utm_medium || null,
    utm_campaign: stored.utm_campaign || null,
    referrer: stored.referrer || null,
    landing_page: stored.landing_page || null,
  };
}

/**
 * Manual reset for tests / debugging.
 */
export function clearCapturedUtms() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
}
